import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ProductTourOverlay } from '../components/tour/ProductTourOverlay';
import { learnerTourService } from '../services/learnerTourService';
import { useAppLocale } from '../providers/LocaleProvider';
import {
  hasSeenTour,
  isManualOnlyLearnerTourPageKey,
  isTourGloballyDisabled,
  markTourSeen,
  setTourGloballyDisabled,
  type LearnerTourPageDefinition,
} from '../learnerTour/learnerTourRegistry';
import type { ActiveTourStep } from '../types/learnerTour';
import {
  PWA_UPDATE_BANNER_VISIBILITY_EVENT,
} from '../utils/pwaUpdateBannerEvents';
import { useHistoryBackClose } from '../hooks/useHistoryBackClose';

const AUTO_START_DELAY_MS = 800;

export const LEARNER_TOUR_PAGE_KEY_EVENT = 'app:learnerTourPageKey';
export const LEARNER_TOUR_OVERLAY_PAGE_KEY_EVENT = 'app:learnerTourOverlayPageKey';

interface ProductTourContextValue {
  pageKey: string | null;
  /** Page under any open modal. Bottom-banner help follows this, not the modal tour. */
  basePageKey: string | null;
  setPageKey: (key: string | null) => void;
  startTour: (force?: boolean) => Promise<void>;
  isRunning: boolean;
}

const ProductTourContext = createContext<ProductTourContextValue | null>(null);

export function useProductTour(): ProductTourContextValue {
  const ctx = useContext(ProductTourContext);
  if (!ctx) {
    return {
      pageKey: null,
      basePageKey: null,
      setPageKey: () => {},
      startTour: async () => {},
      isRunning: false,
    };
  }
  return ctx;
}

interface ProductTourProviderProps {
  children: React.ReactNode;
  /** Initial page key from App view */
  initialPageKey?: string | null;
  /** Whether learner is authenticated */
  enabled?: boolean;
}

export const ProductTourProvider: React.FC<ProductTourProviderProps> = ({
  children,
  initialPageKey = null,
  enabled = true,
}) => {
  const { locale } = useAppLocale();
  const [pageKey, setPageKey] = useState<string | null>(initialPageKey);
  const [basePageKey, setBasePageKey] = useState<string | null>(initialPageKey);
  const [steps, setSteps] = useState<ActiveTourStep[]>([]);
  const [running, setRunning] = useState(false);
  const autoStartedRef = useRef<Set<string>>(new Set());
  const fetchGenRef = useRef(0);
  const basePageKeyRef = useRef<string | null>(initialPageKey);
  const overlayActiveRef = useRef(false);
  const pwaBannerVisibleRef = useRef(false);
  const pendingAutoStartKeyRef = useRef<string | null>(null);
  const pendingManualTourRef = useRef<{ key: string; force: boolean } | null>(null);
  const autoStartTimerRef = useRef<number | null>(null);
  const scheduledTourKeyRef = useRef<string | null>(null);
  const loadAndStartRef = useRef<(key: string, force: boolean) => Promise<void>>(async () => {});
  const pageKeyRef = useRef<string | null>(initialPageKey);
  const runningRef = useRef(false);
  const overlayPageKeyRef = useRef<string | null>(null);
  const inFlightTourKeyRef = useRef<string | null>(null);
  const activeTourKeyRef = useRef<string | null>(null);
  pageKeyRef.current = pageKey;
  runningRef.current = running;

  const clearAutoStartTimer = useCallback(() => {
    if (autoStartTimerRef.current != null) {
      window.clearTimeout(autoStartTimerRef.current);
      autoStartTimerRef.current = null;
    }
  }, []);

  const finishTour = useCallback((opts?: { disableAll?: boolean }) => {
    if (pageKey) markTourSeen(pageKey);
    if (opts?.disableAll) setTourGloballyDisabled();
    runningRef.current = false;
    activeTourKeyRef.current = null;
    setRunning(false);
    setSteps([]);
  }, [pageKey]);

  const dismissTour = useCallback(() => {
    runningRef.current = false;
    activeTourKeyRef.current = null;
    setRunning(false);
    setSteps([]);
  }, []);

  useHistoryBackClose(running, dismissTour, 'product-tour');

  const loadAndStart = useCallback(
    async (key: string, force: boolean) => {
      if (!enabled) return;
      if (pwaBannerVisibleRef.current) {
        if (!force) pendingAutoStartKeyRef.current = key;
        return;
      }
      if (!force && (hasSeenTour(key) || isTourGloballyDisabled())) return;

      const gen = ++fetchGenRef.current;
      inFlightTourKeyRef.current = key;
      const tour = await learnerTourService.getPublicTour(key, locale);
      if (inFlightTourKeyRef.current === key) inFlightTourKeyRef.current = null;
      if (gen !== fetchGenRef.current) return;
      if (pwaBannerVisibleRef.current) {
        if (!force) pendingAutoStartKeyRef.current = key;
        return;
      }
      if (!tour?.steps?.length) {
        autoStartedRef.current.add(key);
        window.dispatchEvent(new CustomEvent('app:productTourHelpSettled'));
        return;
      }

      const activeSteps: ActiveTourStep[] = tour.steps
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((s) => ({
          targetKey: s.targetKey,
          title: s.title,
          body: s.body,
          popoverSide: (s.popoverSide || 'auto') as ActiveTourStep['popoverSide'],
        }));

      autoStartedRef.current.add(key);
      pageKeyRef.current = key;
      activeTourKeyRef.current = key;
      runningRef.current = true;
      setSteps(activeSteps);
      setRunning(true);
      window.dispatchEvent(new CustomEvent('app:productTourRunning'));
    },
    [enabled, locale]
  );

  loadAndStartRef.current = loadAndStart;

  const scheduleTourStart = useCallback(
    (key: string, force: boolean) => {
      clearAutoStartTimer();
      scheduledTourKeyRef.current = key;
      autoStartTimerRef.current = window.setTimeout(() => {
        scheduledTourKeyRef.current = null;
        autoStartTimerRef.current = null;
        void loadAndStartRef.current(key, force);
      }, AUTO_START_DELAY_MS);
    },
    [clearAutoStartTimer]
  );

  const tryStartPendingTour = useCallback(() => {
    if (pwaBannerVisibleRef.current) return;

    const manual = pendingManualTourRef.current;
    if (manual) {
      pendingManualTourRef.current = null;
      scheduleTourStart(manual.key, manual.force);
      return;
    }

    const pendingKey = pendingAutoStartKeyRef.current;
    if (!pendingKey || autoStartedRef.current.has(pendingKey)) return;

    pendingAutoStartKeyRef.current = null;
    autoStartedRef.current.add(pendingKey);
    scheduleTourStart(pendingKey, false);
  }, [scheduleTourStart]);

  const requestTourStart = useCallback(
    (key: string, force: boolean, mode: 'auto' | 'manual') => {
      if (pwaBannerVisibleRef.current) {
        if (mode === 'auto') {
          pendingAutoStartKeyRef.current = key;
        } else {
          pendingManualTourRef.current = { key, force };
        }
        return;
      }

      scheduleTourStart(key, force);
    },
    [scheduleTourStart]
  );

  useEffect(() => {
    const onPwaBanner = (e: Event) => {
      const visible = Boolean(
        (e as CustomEvent<{ visible?: boolean }>).detail?.visible
      );
      pwaBannerVisibleRef.current = visible;
      if (visible) {
        clearAutoStartTimer();
        if (scheduledTourKeyRef.current) {
          pendingAutoStartKeyRef.current = scheduledTourKeyRef.current;
          scheduledTourKeyRef.current = null;
        }
        return;
      }
      tryStartPendingTour();
    };
    window.addEventListener(PWA_UPDATE_BANNER_VISIBILITY_EVENT, onPwaBanner);
    return () => window.removeEventListener(PWA_UPDATE_BANNER_VISIBILITY_EVENT, onPwaBanner);
  }, [tryStartPendingTour]);

  useEffect(() => () => clearAutoStartTimer(), [clearAutoStartTimer]);

  useEffect(() => {
    const next = initialPageKey ?? null;
    basePageKeyRef.current = next;
    setBasePageKey(next);
    if (overlayActiveRef.current) return;
    setPageKey(next);
  }, [initialPageKey]);

  useEffect(() => {
    const onSubPage = (e: Event) => {
      if (overlayActiveRef.current) return;
      const detail = (e as CustomEvent<{ pageKey?: string | null }>).detail;
      if (!detail?.pageKey) return;
      basePageKeyRef.current = detail.pageKey;
      setBasePageKey(detail.pageKey);
      setPageKey(detail.pageKey);
    };
    window.addEventListener(LEARNER_TOUR_PAGE_KEY_EVENT, onSubPage);
    return () => window.removeEventListener(LEARNER_TOUR_PAGE_KEY_EVENT, onSubPage);
  }, []);

  const abandonOverlayTour = useCallback((closedKey: string | null) => {
    if (!closedKey) return;
    if (scheduledTourKeyRef.current === closedKey) {
      clearAutoStartTimer();
      scheduledTourKeyRef.current = null;
    }
    if (pendingManualTourRef.current?.key === closedKey) {
      pendingManualTourRef.current = null;
    }
    if (pendingAutoStartKeyRef.current === closedKey) {
      pendingAutoStartKeyRef.current = null;
    }
    if (inFlightTourKeyRef.current === closedKey) {
      fetchGenRef.current += 1;
      inFlightTourKeyRef.current = null;
    }
    if (runningRef.current && activeTourKeyRef.current === closedKey) {
      runningRef.current = false;
      activeTourKeyRef.current = null;
      setRunning(false);
      setSteps([]);
    }
    window.dispatchEvent(new CustomEvent('app:productTourHelpSettled'));
  }, [clearAutoStartTimer]);

  useEffect(() => {
    const onOverlay = (e: Event) => {
      const detail = (e as CustomEvent<{ pageKey?: string | null; active?: boolean }>).detail;
      if (!detail) return;
      if (detail.active && detail.pageKey) {
        if (!overlayActiveRef.current) {
          basePageKeyRef.current = basePageKeyRef.current ?? initialPageKey ?? null;
        }
        overlayActiveRef.current = true;
        overlayPageKeyRef.current = detail.pageKey;
        setPageKey(detail.pageKey);
        return;
      }
      const closedKey = overlayPageKeyRef.current;
      overlayActiveRef.current = false;
      overlayPageKeyRef.current = null;
      abandonOverlayTour(closedKey);
      setPageKey(basePageKeyRef.current ?? initialPageKey ?? null);
    };
    window.addEventListener(LEARNER_TOUR_OVERLAY_PAGE_KEY_EVENT, onOverlay);
    return () => window.removeEventListener(LEARNER_TOUR_OVERLAY_PAGE_KEY_EVENT, onOverlay);
  }, [initialPageKey, abandonOverlayTour]);

  const startTour = useCallback(
    async (force = true) => {
      if (!pageKey) return;
      requestTourStart(pageKey, force, 'manual');
    },
    [pageKey, requestTourStart]
  );

  // Auto-start on first visit when page key changes
  useEffect(() => {
    if (!enabled || !pageKey || running) return;
    if (isManualOnlyLearnerTourPageKey(pageKey)) {
      autoStartedRef.current.add(pageKey);
      return;
    }
    if (autoStartedRef.current.has(pageKey)) return;
    if (hasSeenTour(pageKey) || isTourGloballyDisabled()) {
      autoStartedRef.current.add(pageKey);
      return;
    }

    requestTourStart(pageKey, false, 'auto');
    return () => clearAutoStartTimer();
  }, [enabled, pageKey, running, requestTourStart, clearAutoStartTimer]);

  // Help button requests tour replay (optional detail.pageKey for modal overlays)
  useEffect(() => {
    const onHelp = (e: Event) => {
      const forcedKey = (e as CustomEvent<{ pageKey?: string }>).detail?.pageKey;
      if (forcedKey) {
        overlayActiveRef.current = true;
        overlayPageKeyRef.current = forcedKey;
        setPageKey(forcedKey);
        requestTourStart(forcedKey, true, 'manual');
        return;
      }
      void startTour(true);
    };
    window.addEventListener('app:requestPageTour', onHelp);
    return () => window.removeEventListener('app:requestPageTour', onHelp);
  }, [requestTourStart, startTour]);

  const value = useMemo(
    (): ProductTourContextValue => ({
      pageKey,
      basePageKey,
      setPageKey,
      startTour,
      isRunning: running,
    }),
    [pageKey, basePageKey, startTour, running]
  );

  return (
    <ProductTourContext.Provider value={value}>
      {children}
      {running && pageKey && steps.length > 0 && (
        <ProductTourOverlay
          pageKey={pageKey}
          steps={steps}
          onComplete={finishTour}
          onSkip={finishTour}
          onDismiss={dismissTour}
        />
      )}
    </ProductTourContext.Provider>
  );
};

/** Notify tour provider of a composite page key (e.g. courses-catalog). */
export function emitLearnerTourPageKey(pageKey: string): void {
  window.dispatchEvent(
    new CustomEvent(LEARNER_TOUR_PAGE_KEY_EVENT, { detail: { pageKey } })
  );
}

/** Switch tour context to a modal overlay page (settings, search) and restore on close. */
export function emitLearnerTourOverlayPageKey(pageKey: string | null, active: boolean): void {
  window.dispatchEvent(
    new CustomEvent(LEARNER_TOUR_OVERLAY_PAGE_KEY_EVENT, {
      detail: { pageKey, active },
    })
  );
}

export type { LearnerTourPageDefinition };
